import type { DiscoveredNode } from '../graph-explorer/home/ObjectsVisualizer/services/cypherQueryService';
import type { InferenceRelationshipOverrides } from '../graph-explorer/home/ObjectsVisualizer/atoms/inferenceAtoms';

export type InferenceResultType =
	| 'all'
	| 'project'
	| 'decision'
	| 'team'
	| 'artifact'
	| 'risk'
	| 'highlight';

export type InferredArtifactRelationshipType =
	| 'atlassian-project-has-related-entity'
	| 'inferred-project-has-related-entity';

export type InferenceReference = {
	ari: string;
	typename: string;
	title: string;
	description?: string;
	url?: string;
	relationshipType?: string;
	relationshipDirection?: 'incoming' | 'outgoing';
	metadata: Record<string, unknown>;
};

export type InferenceGraphRelationship = {
	relationshipType: string;
	source: InferenceReference;
	target: InferenceReference;
};

export type InferenceRecord = InferenceReference & {
	id: string;
	type: Exclude<InferenceResultType, 'all'>;
	lastUpdated: number | null;
	references: InferenceReference[];
	/** Relationships required to make non-hydratable references meaningful in the graph. */
	supportingRelationships?: InferenceGraphRelationship[];
	source?: InferenceReference;
	relationshipMetadata?: Record<string, unknown>;
};

export const isInferredArtifactRecord = (
	record: InferenceRecord,
): record is InferenceRecord & {
	type: 'artifact';
	source: InferenceReference;
	relationshipType: InferredArtifactRelationshipType;
	relationshipMetadata: Record<string, unknown>;
} => record.type === 'artifact';

export type InferenceQueryResponse = {
	data?: {
		graphStore?: {
			cypherQueryV2?: {
				edges?: Array<{
					node?: {
						columns?: Array<{ key?: string; value?: unknown }>;
					};
				}>;
			};
		};
	};
};

export type ParsedQueryPage = {
	records: InferenceRecord[];
	rowCount: number;
	isPartial?: boolean;
};

export const INFERENCE_TYPES = [
	'project',
	'decision',
	'team',
	'artifact',
	'risk',
	'highlight',
] as const;
export type InferenceType = (typeof INFERENCE_TYPES)[number];
export type InferenceResultsCursor = Partial<
	Record<
		InferenceType,
		{
			offset: number;
			records: InferenceRecord[];
			exhausted: boolean;
		}
	>
>;

export type InferenceResultsPage = {
	nextCursor?: InferenceResultsCursor;
	records: InferenceRecord[];
	hasNextPage: boolean;
	isPartial?: boolean;
};

const readString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
	typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const readTimestamp = (value: unknown): number | undefined => {
	const numberValue = readNumber(value);
	if (numberValue !== undefined) {
		return numberValue;
	}
	if (typeof value !== 'string' || value.trim().length === 0) {
		return undefined;
	}
	const numericValue = Number(value);
	if (Number.isFinite(numericValue)) {
		return numericValue;
	}
	const dateValue = Date.parse(value);
	return Number.isFinite(dateValue) ? dateValue : undefined;
};

const sentenceCase = (value: string): string => {
	const normalized = value.replace(/[_-]+/g, ' ').trim().toLocaleLowerCase();
	return normalized ? `${normalized.charAt(0).toLocaleUpperCase()}${normalized.slice(1)}` : '';
};

const fallbackTitle = (ari: string): string => {
	const resource = ari.split(':').pop() ?? ari;
	const identifier = resource.split('/').slice(1).join('/') || resource;
	try {
		return sentenceCase(decodeURIComponent(identifier));
	} catch {
		return sentenceCase(identifier);
	}
};

const readEntityUrl = (metadata: Record<string, unknown>): string | undefined => {
	const directUrl = readString(metadata.webUrl) ?? readString(metadata.url);
	if (directUrl) {
		return directUrl;
	}

	const links = metadata.links;
	if (!links || typeof links !== 'object') {
		return undefined;
	}
	const base = readString((links as Record<string, unknown>).base);
	const webUi = readString((links as Record<string, unknown>).webUi);
	return base && webUi ? `${base}${webUi}` : undefined;
};

const entityUrlAliasKeys = ['loomUrl', 'townsquareProjectUrl', 'townsquareGoalUrl'] as const;

const normalizeEntityUrlAliases = (metadata: Record<string, unknown>): Record<string, unknown> => {
	const normalizedMetadata = { ...metadata };
	const aliases = [
		...entityUrlAliasKeys.map((key) => [key, 'url'] as const),
		...['decisionSubtype', 'riskSubtype', 'highlightSubtype'].map(
			(key) => [key, 'subtype'] as const,
		),
	];
	for (const [alias, field] of aliases) {
		if (Object.prototype.hasOwnProperty.call(metadata, alias)) {
			normalizedMetadata[field] = metadata[alias];
			delete normalizedMetadata[alias];
		}
	}
	return normalizedMetadata;
};
const unwrapNode = (value: unknown, fallbackTypename?: string): InferenceReference | null => {
	if (!value || typeof value !== 'object') {
		return null;
	}
	const wrapper = value as { id?: unknown; data?: unknown };
	const ari = readString(wrapper.id);
	if (!ari) {
		return null;
	}
	if (!wrapper.data || typeof wrapper.data !== 'object') {
		return fallbackTypename
			? {
					ari,
					typename: fallbackTypename,
					title: fallbackTitle(ari),
					metadata: {},
				}
			: null;
	}

	const data = wrapper.data as Record<string, unknown>;
	const isStoredNode = data.__typename === 'GraphStoreNodeResponse';
	const rawMetadata =
		isStoredNode && data.metadata && typeof data.metadata === 'object'
			? (data.metadata as Record<string, unknown>)
			: data;
	const metadata = normalizeEntityUrlAliases(rawMetadata);
	const typename =
		(isStoredNode ? readString(data.nodeType) : readString(data.__typename)) ?? 'Unknown';
	const title =
		readString(metadata.title) ??
		readString(metadata.name) ??
		readString(metadata.key) ??
		fallbackTitle(ari);
	const description = readString(metadata.summary) ?? readString(metadata.description);
	const url = readEntityUrl(metadata);

	return {
		ari,
		typename,
		title,
		description,
		url,
		metadata: { ...metadata },
	};
};

const unwrapNodes = (value: unknown, fallbackTypename?: string): InferenceReference[] => {
	if (!value || typeof value !== 'object') {
		return [];
	}
	const nodes = (value as { nodes?: unknown }).nodes;
	if (Array.isArray(nodes)) {
		return nodes
			.map((node) => unwrapNode(node, fallbackTypename))
			.filter((node): node is InferenceReference => node !== null);
	}
	const node = unwrapNode(value, fallbackTypename);
	return node ? [node] : [];
};

const getColumnValue = (columns: Array<{ key?: string; value?: unknown }>, key: string): unknown =>
	columns.find((column) => column.key === key)?.value;

const mergeReferences = (
	current: InferenceReference[],
	next: InferenceReference | null,
): InferenceReference[] => {
	if (
		!next ||
		current.some(
			(reference) =>
				reference.ari === next.ari &&
				reference.relationshipType === next.relationshipType &&
				reference.relationshipDirection === next.relationshipDirection,
		)
	) {
		return current;
	}
	return [...current, next];
};

type RelationshipColumn = {
	key: string;
	relationshipType: string;
	direction: 'incoming' | 'outgoing';
	fallbackTypename?: string;
};

const relationshipColumnsByType: Record<
	Exclude<InferenceResultType, 'all' | 'artifact'>,
	RelationshipColumn[]
> = {
	project: [
		{
			key: 'linkedEntities',
			relationshipType: 'inferred-project-links-entity',
			direction: 'outgoing',
		},
		{
			key: 'inferredTeams',
			relationshipType: 'inferred-team-collaborates-on-inferred-project',
			direction: 'incoming',
			fallbackTypename: 'InferredTeam',
		},
		{
			key: 'inferredTeam',
			relationshipType: 'inferred-team-collaborates-on-inferred-project',
			direction: 'incoming',
			fallbackTypename: 'InferredTeam',
		},
		{
			key: 'owners',
			relationshipType: 'atlassian-user-owns-inferred-project',
			direction: 'incoming',
			fallbackTypename: 'IdentityUser',
		},
		{
			key: 'collaborators',
			relationshipType: 'atlassian-user-collaborates-on-inferred-project',
			direction: 'incoming',
			fallbackTypename: 'IdentityUser',
		},
		{
			key: 'referenceEntity',
			relationshipType: 'inferred-project-links-entity',
			direction: 'outgoing',
		},
	],
	decision: [
		{
			key: 'linkedEntities',
			relationshipType: 'inferred-decision-links-entity',
			direction: 'outgoing',
		},
		{
			key: 'impactedEntities',
			relationshipType: 'inferred-decision-impacts-entity',
			direction: 'outgoing',
		},
		{
			key: 'referenceEntity',
			relationshipType: 'inferred-decision-links-entity',
			direction: 'outgoing',
		},
	],
	team: [
		{
			key: 'jp',
			relationshipType: 'inferred-team-links-entity',
			direction: 'outgoing',
			fallbackTypename: 'JiraProject',
		},
		{
			key: 'ip',
			relationshipType: 'inferred-team-collaborates-on-inferred-project',
			direction: 'outgoing',
			fallbackTypename: 'InferredProject',
		},
	],
	risk: [
		{
			key: 'linkedEntities',
			relationshipType: 'inferred-risk-links-entity',
			direction: 'outgoing',
		},
		{
			key: 'impactedEntities',
			relationshipType: 'inferred-risk-impacts-entity',
			direction: 'outgoing',
		},
	],
	highlight: [
		{
			key: 'linkedEntities',
			relationshipType: 'inferred-highlight-links-entity',
			direction: 'outgoing',
		},
		{
			key: 'impactedEntities',
			relationshipType: 'inferred-highlight-impacts-entity',
			direction: 'outgoing',
		},
	],
};

const getRowReferences = (
	columns: Array<{ key?: string; value?: unknown }>,
	type: Exclude<InferenceResultType, 'all' | 'artifact'>,
): InferenceReference[] =>
	relationshipColumnsByType[type].flatMap(
		({ key, relationshipType, direction, fallbackTypename }) =>
			unwrapNodes(getColumnValue(columns, key), fallbackTypename).map((reference) => ({
				...reference,
				relationshipType,
				relationshipDirection: direction,
			})),
	);

const getRowSupportingRelationships = (
	columns: Array<{ key?: string; value?: unknown }>,
	type: Exclude<InferenceResultType, 'all' | 'artifact'>,
): InferenceGraphRelationship[] => {
	if (type !== 'project') {
		return [];
	}

	const inferredTeams = unwrapNodes(getColumnValue(columns, 'inferredTeam'), 'InferredTeam');
	const backingEntities = unwrapNodes(getColumnValue(columns, 'inferredTeamBacking'));

	return inferredTeams.flatMap((inferredTeam) =>
		backingEntities.map((backingEntity) => ({
			relationshipType: 'inferred-team-links-entity',
			source: inferredTeam,
			target: backingEntity,
		})),
	);
};

const mergeSupportingRelationships = (
	current: InferenceGraphRelationship[],
	next: InferenceGraphRelationship,
): InferenceGraphRelationship[] =>
	current.some(
		(relationship) =>
			relationship.relationshipType === next.relationshipType &&
			relationship.source.ari === next.source.ari &&
			relationship.target.ari === next.target.ari,
	)
		? current
		: [...current, next];

const getLastUpdated = (node: InferenceReference): number | null =>
	readTimestamp(node.metadata.lastUpdated) ??
	readTimestamp(node.metadata.updatedAt) ??
	readTimestamp(node.metadata.latestUpdateDate) ??
	null;

const unwrapScalarValue = (value: unknown): unknown => {
	if (!value || typeof value !== 'object') {
		return value;
	}
	const scalar = value as { stringValue?: unknown; intValue?: unknown; value?: unknown };
	return scalar.stringValue ?? scalar.intValue ?? scalar.value;
};

const parseInferredArtifactQueryResponse = (response: InferenceQueryResponse): ParsedQueryPage => {
	const edges = response.data?.graphStore?.cypherQueryV2?.edges ?? [];
	const recordsById = new Map<string, InferenceRecord>();

	edges.forEach((edge) => {
		const columns = edge.node?.columns ?? [];
		const source = unwrapNode(getColumnValue(columns, 'project'));
		const artifact = unwrapNode(getColumnValue(columns, 'artifact'));
		if (!source || !artifact) {
			return;
		}

		const relationshipType: InferredArtifactRelationshipType =
			source.typename === 'InferredProject'
				? 'inferred-project-has-related-entity'
				: 'atlassian-project-has-related-entity';
		const relevance = readString(unwrapScalarValue(getColumnValue(columns, 'relevance')));
		const appId = readString(unwrapScalarValue(getColumnValue(columns, 'appId')));
		const appVersion = readNumber(unwrapScalarValue(getColumnValue(columns, 'appVersion')));
		const relationshipMetadata: Record<string, unknown> = {
			...(relevance ? { relevance } : {}),
			...(appId && appVersion !== undefined ? { provenance: { appId, appVersion } } : {}),
		};
		const relationshipEvidenceKey = [relevance, appId, appVersion]
			.map((value) => encodeURIComponent(String(value ?? '')))
			.join(':');
		const id = `artifact:${relationshipType}:${source.ari}:${artifact.ari}:${relationshipEvidenceKey}`;
		const lastUpdated = Math.max(getLastUpdated(source) ?? 0, getLastUpdated(artifact) ?? 0);

		recordsById.set(id, {
			...artifact,
			id,
			type: 'artifact',
			description: artifact.description ?? `Related to ${source.title}.`,
			lastUpdated: lastUpdated || null,
			references: [
				{
					...source,
					relationshipType,
					relationshipDirection: 'incoming',
				},
			],
			source,
			relationshipType,
			relationshipMetadata,
		});
	});

	return {
		records: [...recordsById.values()].sort(
			(left, right) => (right.lastUpdated ?? 0) - (left.lastUpdated ?? 0),
		),
		// Raw Cypher rows determine whether another page may exist.
		rowCount: edges.length,
	};
};

export const parseInferenceQueryResponse = (
	response: InferenceQueryResponse,
	type: Exclude<InferenceResultType, 'all'>,
): ParsedQueryPage => {
	if (type === 'artifact') {
		return parseInferredArtifactQueryResponse(response);
	}
	const edges = response.data?.graphStore?.cypherQueryV2?.edges ?? [];
	const recordsById = new Map<string, InferenceRecord>();
	// Cypher pages primary nodes before expanding their relationships. Count those
	// nodes before hydration/parsing, so inaccessible metadata cannot end paging.
	const primaryIds = new Set<string>();
	let unidentifiedRows = 0;

	edges.forEach((edge) => {
		const columns = edge.node?.columns ?? [];
		const primaryKey =
			type === 'team' ? 'it' : type === 'risk' ? 'ir' : type === 'highlight' ? 'ih' : 'ip';
		const rawPrimary = getColumnValue(columns, primaryKey);
		const primaryId =
			rawPrimary && typeof rawPrimary === 'object'
				? readString((rawPrimary as { id?: unknown }).id)
				: undefined;
		if (primaryId) {
			primaryIds.add(primaryId);
		} else {
			unidentifiedRows += 1;
		}
		const primary = unwrapNode(
			getColumnValue(columns, primaryKey),
			type === 'team' ? 'InferredTeam' : undefined,
		);
		const rowReferences = getRowReferences(columns, type);
		const rowSupportingRelationships = getRowSupportingRelationships(columns, type);
		if (!primary) {
			return;
		}

		const id = `${type}:${primary.ari}`;
		const current = recordsById.get(id);
		const references = rowReferences.reduce<InferenceReference[]>(
			(references, reference) => mergeReferences(references, reference),
			current?.references ?? [],
		);
		const supportingRelationships = rowSupportingRelationships.reduce<InferenceGraphRelationship[]>(
			(relationships, relationship) => mergeSupportingRelationships(relationships, relationship),
			current?.supportingRelationships ?? [],
		);
		const backingProject =
			type === 'team'
				? references.find((reference) => reference.typename === 'JiraProject')
				: undefined;
		const lastUpdated = Math.max(
			current?.lastUpdated ?? 0,
			getLastUpdated(primary) ?? 0,
			...references.map((item) => getLastUpdated(item) ?? 0),
			...supportingRelationships.flatMap(({ source, target }) => [
				getLastUpdated(source) ?? 0,
				getLastUpdated(target) ?? 0,
			]),
		);
		const backingProjectKey = backingProject ? readString(backingProject.metadata.key) : undefined;

		recordsById.set(id, {
			...primary,
			id,
			type,
			title: backingProject?.title ?? primary.title,
			description:
				primary.description ??
				backingProject?.description ??
				(type === 'team'
					? 'Inferred from collaboration on active inferred projects.'
					: 'No summary is stored for this inference.'),
			lastUpdated: lastUpdated || null,
			references,
			...(supportingRelationships.length > 0 ? { supportingRelationships } : {}),
			metadata: {
				...primary.metadata,
				...(backingProjectKey ? { backingProjectKey } : {}),
			},
		});
	});

	return {
		records: [...recordsById.values()].sort(
			(left, right) => (right.lastUpdated ?? 0) - (left.lastUpdated ?? 0),
		),
		rowCount: primaryIds.size + unidentifiedRows,
	};
};

export const mergeInferenceRecords = (pages: ParsedQueryPage[]): InferenceRecord[] =>
	pages
		.flatMap((page) => page.records)
		.sort((left, right) => (right.lastUpdated ?? 0) - (left.lastUpdated ?? 0));

const toGraphNode = (node: InferenceReference): Record<string, unknown> => ({
	...node.metadata,
	id: node.ari,
	__typename: node.typename,
	title: node.title,
	name: node.title,
	description: node.description,
	url: node.url,
});

export const getInferenceRelationshipType = (
	type: InferenceRecord['type'],
	reference: InferenceReference,
): string => {
	if (reference.relationshipType) {
		return reference.relationshipType;
	}
	if (type === 'decision') {
		return 'inferred-decision-links-entity';
	}
	if (type === 'team') {
		return reference.typename === 'JiraProject'
			? 'inferred-team-links-entity'
			: 'inferred-team-collaborates-on-inferred-project';
	}
	if (type === 'risk') {
		return 'inferred-risk-links-entity';
	}
	if (type === 'highlight') {
		return 'inferred-highlight-links-entity';
	}
	return 'inferred-project-links-entity';
};

type RelationshipAccumulator = Map<string, Map<string, DiscoveredNode>>;

const toDiscoveredNode = (node: InferenceReference): DiscoveredNode => ({
	id: node.ari,
	typename: node.typename,
	displayName: node.title,
	url: node.url ?? null,
	properties: node.metadata,
});

/**
 * Builds the relationship view for an inference-detail graph. The generic
 * Explorer deliberately filters inferred relationship types from live
 * discovery, so the detail graph supplies its authoritative references
 * directly and caches both ends of every known edge.
 */
export const buildInferenceRelationshipOverrides = (
	record: InferenceRecord,
): InferenceRelationshipOverrides => {
	const accumulators: Record<
		string,
		{ incoming: RelationshipAccumulator; outgoing: RelationshipAccumulator }
	> = {};

	const ensureNode = (node: InferenceReference) => {
		if (!accumulators[node.ari]) {
			accumulators[node.ari] = {
				incoming: new Map(),
				outgoing: new Map(),
			};
		}
	};

	const addRelationship = (
		source: InferenceReference,
		target: InferenceReference,
		relationshipType: string,
	) => {
		ensureNode(source);
		ensureNode(target);
		const outgoingByType = accumulators[source.ari].outgoing;
		const outgoingNodes = outgoingByType.get(relationshipType) ?? new Map();
		outgoingNodes.set(target.ari, toDiscoveredNode(target));
		outgoingByType.set(relationshipType, outgoingNodes);

		const incomingByType = accumulators[target.ari].incoming;
		const incomingNodes = incomingByType.get(relationshipType) ?? new Map();
		incomingNodes.set(source.ari, toDiscoveredNode(source));
		incomingByType.set(relationshipType, incomingNodes);
	};

	ensureNode(record);
	for (const reference of record.references) {
		const isIncoming = reference.relationshipDirection === 'incoming';
		const source = isIncoming ? reference : record;
		const target = isIncoming ? record : reference;
		addRelationship(source, target, getInferenceRelationshipType(record.type, reference));
	}

	for (const relationship of record.supportingRelationships ?? []) {
		addRelationship(relationship.source, relationship.target, relationship.relationshipType);
	}

	return Object.fromEntries(
		Object.entries(accumulators).map(([ari, { incoming, outgoing }]) => [
			ari,
			{
				incoming: [...incoming.entries()].map(([type, nodes]) => ({
					type,
					count: nodes.size,
					nodes: [...nodes.values()],
				})),
				outgoing: [...outgoing.entries()].map(([type, nodes]) => ({
					type,
					count: nodes.size,
					nodes: [...nodes.values()],
				})),
			},
		]),
	);
};

/**
 * Projects one selected row into the response shape consumed by DTAC's existing ObjectsVisualizer.
 */
export const buildInferenceGraphResponse = (record: InferenceRecord): InferenceQueryResponse => {
	const sourceNode = toGraphNode(record);
	const referenceRows = record.references.map((reference) => {
		const relationshipType = getInferenceRelationshipType(record.type, reference);
		const referenceNode = toGraphNode(reference);
		const isIncoming = reference.relationshipDirection === 'incoming';
		const relationshipSource = isIncoming ? referenceNode : sourceNode;
		const relationshipTarget = isIncoming ? sourceNode : referenceNode;
		return {
			node: {
				columns: [
					{
						key: 'relationship',
						value: {
							id: `${relationshipType}:${relationshipSource.id}:${relationshipTarget.id}`,
							__typename: relationshipType,
							sourceNode: relationshipSource,
							targetNode: relationshipTarget,
						},
					},
				],
			},
		};
	});
	const supportingRows = (record.supportingRelationships ?? []).map(
		({ relationshipType, source, target }) => {
			const relationshipSource = toGraphNode(source);
			const relationshipTarget = toGraphNode(target);
			return {
				node: {
					columns: [
						{
							key: 'relationship',
							value: {
								id: `${relationshipType}:${relationshipSource.id}:${relationshipTarget.id}`,
								__typename: relationshipType,
								sourceNode: relationshipSource,
								targetNode: relationshipTarget,
							},
						},
					],
				},
			};
		},
	);
	const relationshipRows = [...referenceRows, ...supportingRows];
	const rows = relationshipRows.length
		? relationshipRows
		: [{ node: { columns: [{ key: 'inference', value: sourceNode }] } }];

	return { data: { graphStore: { cypherQueryV2: { edges: rows } } } };
};
